import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Lock, Clock, Shield, Star, CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const HeroSection = ({ onBookClick }) => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1610641819167-e9f4f393a7fe?q=80&w=2070"
          alt="Golfo di Napoli con il Vesuvio"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/60 to-[#0A0A0A]" />
      </div>

      {/* Content */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="relative z-10 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto w-full pt-32 pb-20"
      >
        {/* Badge */}
        <motion.div variants={itemVariants} className="mb-6">
          <span className="inline-flex items-center gap-2 text-[#FFCC00] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase">
            <MapPin className="w-4 h-4" />
            Napoli, Italia
          </span>
        </motion.div>

        {/* Main Heading */}
        <motion.h1
          variants={itemVariants}
          className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] max-w-4xl mb-6"
        >
          {t('hero.title')}
          <br />
          <span className="text-[#FFCC00]">{t('hero.titleHighlight')}</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          variants={itemVariants}
          className="text-gray-400 text-base sm:text-lg md:text-xl max-w-2xl mb-8"
        >
          {t('hero.subtitle')} {t('hero.startingFrom')} <span className="text-[#FFCC00] font-semibold">€1,50{t('hero.perHour')}</span>.
        </motion.p>

        {/* Urgency Banner */}
        <motion.div
          variants={itemVariants}
          className="mb-8 inline-flex items-center gap-2 bg-[#FFCC00]/10 border border-[#FFCC00]/30 px-4 py-2 text-[#FFCC00] text-sm"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFCC00] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FFCC00]"></span>
          </span>
          <span>25 {t('hero.availableToday')}</span>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={onBookClick}
            data-testid="hero-book-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide hover:bg-[#E6B800] px-8 py-4 transition-all duration-200"
          >
            <CalendarDays className="w-5 h-5" />
            {t('hero.bookNow')}
          </button>
          <a
            href="#come-funziona"
            data-testid="hero-how-it-works"
            className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/20 hover:border-[#FFCC00] hover:text-[#FFCC00] px-8 py-4 transition-all duration-200"
          >
            {t('hero.seeHow')}
          </a>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          variants={itemVariants}
          className="mt-12 flex flex-wrap items-center gap-6 text-gray-500 text-sm"
        >
          <div className="flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] px-4 py-2">
            <Shield className="w-5 h-5 text-[#FFCC00]" />
            <span>{t('hero.insurance')}</span>
          </div>
          <div className="flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] px-4 py-2">
            <Lock className="w-5 h-5 text-[#FFCC00]" />
            <span>{t('hero.security247')}</span>
          </div>
          <div className="flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] px-4 py-2">
            <Clock className="w-5 h-5 text-[#FFCC00]" />
            <span>{t('hero.hours')}</span>
          </div>
        </motion.div>

        {/* Google Reviews Badge */}
        <motion.div
          variants={itemVariants}
          className="mt-6 flex items-center gap-3"
        >
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={`hero-star-${i}`} className="w-5 h-5 fill-[#FFCC00] text-[#FFCC00]" />
            ))}
          </div>
          <span className="text-white font-semibold">4.9/5</span>
          <span className="text-gray-500">• 127+ {t('hero.reviews')}</span>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-[#FFCC00] rounded-full mt-2"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
